import { addActionBarItem } from "@vendure/admin-ui/core";
import gql from "graphql-tag";
import { firstValueFrom, map, type Observable } from "rxjs";

const ORDER_DOCUMENT_DOWNLOADS_QUERY = gql`
  query OrderDocumentDownloads($orderId: ID!) {
    orderDocumentDownloads(orderId: $orderId) {
      orderSummaryBase64
      deliverySlipBase64
      fileBaseName
    }
  }
`;

const RESEND_INTERNAL_EMAIL_MUTATION = gql`
  mutation ResendOrderInternalEmail($orderId: ID!) {
    resendOrderInternalEmail(orderId: $orderId)
  }
`;

type OrderDocumentDownloads = {
  orderSummaryBase64: string;
  deliverySlipBase64: string;
  fileBaseName: string;
};

type DocumentField = "orderSummaryBase64" | "deliverySlipBase64";

type OrderActionContext = {
  entity$: Observable<{ id?: string | number } | undefined>;
  dataService: {
    query: (query: unknown, variables?: Record<string, unknown>, fetchPolicy?: string) => {
      mapSingle: <T>(mapFn: (data: unknown) => T) => Observable<T>;
    };
  };
  notificationService: {
    error: (message: string) => void;
  };
};

type ResendActionContext = {
  entity$: Observable<{ id?: string | number } | undefined>;
  dataService: {
    mutate: (mutation: unknown, variables?: Record<string, unknown>) => Observable<unknown>;
  };
  notificationService: {
    error: (message: string) => void;
    success: (message: string) => void;
  };
};

function base64ToPdfBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: "application/pdf" });
}

// Laedt das PDF authentifiziert ueber die Admin-API (Base64) und stoesst einen
// Download an. Die Dokumente sind NICHT mehr ueber eine oeffentliche URL erreichbar.
async function downloadOrderDocument(
  context: OrderActionContext,
  field: DocumentField,
  filePrefix: string,
  label: string
): Promise<void> {
  const entity = await firstValueFrom(context.entity$);
  const orderId = entity?.id;

  if (!orderId) {
    context.notificationService.error("Bestellung konnte nicht geladen werden.");
    return;
  }

  try {
    const result = await firstValueFrom(
      context.dataService
        .query(ORDER_DOCUMENT_DOWNLOADS_QUERY, { orderId: String(orderId) }, "network-only")
        .mapSingle(data => (data as { orderDocumentDownloads: OrderDocumentDownloads }).orderDocumentDownloads)
    );

    const blob = base64ToPdfBlob(result[field]);
    const url = URL.createObjectURL(blob);
    // document über globalThis, damit der Backend-Typecheck ohne DOM-Lib durchläuft
    // (die Datei wird zur Laufzeit im Browser/Admin ausgeführt).
    const browserDoc = (globalThis as {
      document?: {
        createElement(tag: string): { href: string; download: string; click(): void; remove(): void };
        body: { appendChild(node: unknown): void };
      };
    }).document;
    if (browserDoc) {
      const anchor = browserDoc.createElement("a");
      anchor.href = url;
      anchor.download = `${filePrefix}-${result.fileBaseName}.pdf`;
      browserDoc.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
    URL.revokeObjectURL(url);
  } catch (error) {
    context.notificationService.error(`${label} konnte nicht erstellt werden.`);
    console.error(error);
  }
}

// Sendet die interne info@-Bestellmail ueber die Admin-API erneut. Fragt vorher
// kurz nach (versehentliches Doppel-Senden vermeiden) und meldet das Ergebnis.
async function resendInternalEmail(context: ResendActionContext): Promise<void> {
  const entity = await firstValueFrom(context.entity$);
  const orderId = entity?.id;

  if (!orderId) {
    context.notificationService.error("Bestellung konnte nicht geladen werden.");
    return;
  }

  const confirmed = (globalThis as { confirm?: (message: string) => boolean }).confirm?.(
    "Interne Bestellmail (info@) für diese Bestellung erneut senden? Der Kunde erhält dadurch keine zweite Bestätigung."
  );
  if (confirmed === false) {
    return;
  }

  try {
    await firstValueFrom(context.dataService.mutate(RESEND_INTERNAL_EMAIL_MUTATION, { orderId: String(orderId) }));
    context.notificationService.success("Interne Bestellmail wurde erneut versendet.");
  } catch (error) {
    context.notificationService.error("Interne Bestellmail konnte nicht versendet werden.");
    console.error(error);
  }
}

export default [
  addActionBarItem({
    id: "resend-order-internal-email",
    label: "Bestellmail erneut senden",
    locationId: "order-detail",
    icon: "email",
    buttonStyle: "outline",
    buttonState: context =>
      context.entity$.pipe(
        map(entity => ({
          visible: Boolean(entity?.id),
          disabled: !entity?.id
        }))
      ),
    onClick: (_event, context) => {
      void resendInternalEmail(context as ResendActionContext);
    },
    requiresPermission: ["UpdateOrder"]
  }),
  addActionBarItem({
    id: "download-order-summary-pdf",
    label: "Bestell-PDF",
    locationId: "order-detail",
    icon: "download",
    buttonStyle: "outline",
    buttonState: context =>
      context.entity$.pipe(
        map(entity => ({
          visible: Boolean(entity?.id),
          disabled: !entity?.id
        }))
      ),
    onClick: (_event, context) => {
      void downloadOrderDocument(context as OrderActionContext, "orderSummaryBase64", "Bestellblock", "Bestell-PDF");
    },
    requiresPermission: ["ReadOrder"]
  }),
  addActionBarItem({
    id: "download-delivery-slip-pdf",
    label: "Liefer-PDF",
    locationId: "order-detail",
    icon: "download",
    buttonStyle: "outline",
    buttonState: context =>
      context.entity$.pipe(
        map(entity => ({
          visible: Boolean(entity?.id),
          disabled: !entity?.id
        }))
      ),
    onClick: (_event, context) => {
      void downloadOrderDocument(context as OrderActionContext, "deliverySlipBase64", "Lieferschein", "Liefer-PDF");
    },
    requiresPermission: ["ReadOrder"]
  })
];
